import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  Package,
  Users,
  Settings,
  LogOut,
  Palette,
  Image,
  FileText,
  Home,
  Menu,
  X
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarOpen && !e.target.closest('.admin-sidebar') && !e.target.closest('.menu-toggle')) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [sidebarOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Package, label: 'Produtos', path: '/admin/products' },
    { icon: Users, label: 'Parceiros', path: '/admin/parceiros' },
    { icon: Home, label: 'Homepage', path: '/admin/homepage' },
    { icon: Menu, label: 'Menu', path: '/admin/menu' },
    { icon: Palette, label: 'Aparência', path: '/admin/appearance' },
    { icon: Image, label: 'Imagens', path: '/admin/images' },
    { icon: FileText, label: 'Mensagens', path: '/admin/messages' },
    { icon: Settings, label: 'Configurações', path: '/admin/settings' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#1a1412] border-b border-[#cc8b3a]/30 px-4 py-3 flex items-center justify-between">
        <Link to="/admin/dashboard" className="flex items-center">
          <h1 className="text-xl font-bold text-[#cc8b3a]">Santa Carne</h1>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="menu-toggle p-2 text-white hover:bg-[#cc8b3a]/20 rounded-lg transition-colors"
          aria-label={sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#1a1412] border-r border-[#cc8b3a]/30 flex flex-col transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-[#cc8b3a]/20 flex items-center justify-between">
          <Link to="/admin/dashboard" onClick={() => setSidebarOpen(false)}>
            <h1 className="text-2xl font-bold text-[#cc8b3a]">Santa Carne</h1>
            <p className="text-sm text-white/60">Painel Administrativo</p>
          </Link>
          {/* Close button for mobile */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-white/60 hover:text-white hover:bg-[#cc8b3a]/20 rounded-lg transition-colors"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? 'bg-[#cc8b3a] text-black font-bold'
                    : 'text-white/80 hover:bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-[#cc8b3a]/20">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-bold text-white">{user?.name || 'Admin'}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-[#cc8b3a]/30 text-white hover:bg-[#cc8b3a] hover:text-black"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto lg:ml-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
